/**
 * Responsibility: say whether something would be standing on the railway.
 *
 * ## Why this is a separate thing from the corridor
 *
 * The corridor decides the shape of the *ground*. This decides what may be *built* on it, and the two want
 * different widths: the corridor reaches hundreds of units out to blend a cutting back into the hillside,
 * while a tree only has to keep off the formation and a little either side. A wooded cutting is good. A tree
 * between the rails is not.
 *
 * ## What it plugs into
 *
 * Nothing. That is the point — every biome in the world already consults the cross-biome reservation before it
 * places anything, the way `river.js` notes that one line kept "trees, towers, rock columns, farm buildings and
 * dishes" out of the water with nothing in any other biome having to change. A railway is the same kind of
 * claim, so it goes in through the same door.
 *
 * ## It claims the ground, not merely the space above it
 *
 * The fairground may outrank another biome's *structures*, because two biomes that both consult each other can
 * settle who yields. There is nothing on the other side of a railway to settle with, exactly as there is
 * nothing on the other side of a lake — so this is a ground claim and cannot be outranked. A fairground with a
 * railway through it is not a rarer fairground, it is a broken one.
 *
 * ## What it deliberately does not stop
 *
 * Anything that crosses *over*. A power line's span is drawn between its pylons, so keeping the pylons off the
 * track leaves the cables crossing it with nothing here having to know about them — which is the right answer
 * and cost nothing. A road bridge is the case this does not yet handle: its deck could clear the rails easily,
 * but its abutments are tested with the same query as its deck, so a crossing near the line is refused whole.
 * That costs an occasional bridge and never puts one through a train.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { RAILWAY } from '../config.js';

/**
 * Build the test that says whether a footprint touches the railway.
 *
 * @param {object} options
 * @param {{nearest: Function}} options.surveyor - From `createSurveyor`. Must already be walked past the
 *   ground being asked about, the same invariant the corridor has — see `RAILWAY.PREPARE_MARGIN`.
 * @param {object} [options.tuning] - Overrides for the `RAILWAY` config block.
 * @returns {(x: number, z: number, radius: number) => boolean}
 */
export function createTrackClearance({ surveyor, tuning: overrides = RAILWAY }) {
  const tuning = overrides === RAILWAY ? RAILWAY : { ...RAILWAY, ...overrides };

  if (!surveyor || typeof surveyor.nearest !== 'function') {
    throw new Error('createTrackClearance: surveyor must expose nearest');
  }

  const clearance = tuning.TRACK_CLEARANCE;

  function onTheTrack(x, z, radius = 0) {
    const track = surveyor.nearest(x, z);

    /*
      Null away from the line, which is almost everywhere and is why this is affordable: the surveyor's buckets
      answer that with a lookup and no arithmetic. Every tree in the world asks this once.
    */
    if (track === null) return false;

    return track.lateral < clearance + radius;
  }

  /*
    Marks this as a claim on the ground itself rather than on something standing on it, so a biome allowed to
    outrank other biomes' structures still cannot build here. See the note above, and `isReserved` in
    `tileContent.js`.
  */
  onTheTrack.claimsGround = true;

  return onTheTrack;
}
